import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fastDomainCheckRequest,
  resetScanState,
  getScansRequest,
} from "../../redux/actions/myDomainScanActions";
import { toast } from "react-toastify";
import { FaDownload, FaSearch } from "react-icons/fa";
import { MdCheckCircle } from "react-icons/md";
import Pagination from "../../helper/Pagination";

const MyDomainScan = () => {
  const dispatch = useDispatch();
  const {
    loading: tableLoading = false,
    scanLoading = false,
    data,
    error,
    scans = [],
    pagination = {},
  } = useSelector((state) => state.scan || {});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const user = JSON.parse(sessionStorage.getItem("user"));
  const oemPartnerId = user?.oemPartner?.id;
  const [formData, setFormData] = useState({
    email: "",
    language: "eng",
    domain: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "domain") {
      // Prevent leading spaces but allow spaces after valid characters
      if (value.length === 0 || (value.length > 0 && value[0] !== " ")) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      } else if (formData.domain.length > 0) {
        // Allow spaces if there's already text
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.domain || !formData.email) {
      toast.error("Domain and Email are required", {
        toastId: "validation-required-fields",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Clean domain by removing protocol, www, and trailing slashes
    let cleanedDomain = formData.domain
      .trim()
      .replace(/^https?:\/\//, "") // Remove http:// or https://
      .replace(/^www\./, "") // Remove www.
      .replace(/\/$/, ""); // Remove trailing slash

    // Validate domain format - allows subdomains and multiple dots
    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

    if (!cleanedDomain || !domainRegex.test(cleanedDomain)) {
      toast.error("Invalid domain", {
        toastId: "validation-invalid-domain",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    dispatch(fastDomainCheckRequest({ ...formData, domain: cleanedDomain, oemPartnerId }));
  };

  const handleReset = () => {
    setFormData({
      displayLanguage: "eng",
      email: "",
      language: "eng",
      domain: "",
    });
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(
      getScansRequest({
        limit: 10,
        page: currentPage,
        search: searchTerm,
        oemPartnerId: oemPartnerId
      }),
    );
  }, [dispatch, currentPage, searchTerm]);

  useEffect(() => {
    if (data && !scanLoading) {
      setFormData({
        displayLanguage: "eng",
        email: "",
        language: "eng",
        domain: "",
      });
      dispatch(
        getScansRequest({
          limit: 10,
          page: 1,
          search: "",
          oemPartnerId: oemPartnerId
        }),
      );
      dispatch(resetScanState());
    }
  }, [data]);

  return (
    <div className="page-container">
      <div className="page-card scroll-container">
        <div className="form-header">
          <h2>Domain Scan</h2>
        </div>
        <div className="input-form-section">
          <form onSubmit={handleSubmit} className="input-form">
            <div className="row">
              <div className="col-12 col-md-4 mb-3">
                <div className="form-group">
                  <label htmlFor="domain">
                    Domain <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="domain"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter domain (e.g., example.com)"
                    required
                  />
                </div>
              </div>

              <div className="col-12 col-md-4 mb-3">
                <div className="form-group">
                  <label htmlFor="email">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter email address"
                    required
                  />
                </div>
              </div>

              <div className="col-12 col-md-4 mb-3">
                <div className="form-group">
                  <label htmlFor="language">Report Language</label>
                  <input
                    type="text"
                    id="language"
                    name="language"
                    value="English"
                    className="form-input"
                    disabled
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="form-actions mt-3 d-flex flex-column flex-sm-row gap-2">
              {(formData.domain || formData.email || formData.language) && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="cancel-btn"
                >
                  Reset
                </button>
              )}
              <button type="submit" disabled={scanLoading} className="add-btn">
                {scanLoading ? "Scanning..." : "Start Scan"}
              </button>
            </div>
          </form>
        </div>

        <div className="table-section">
          <div className="position-relative d-flex justify-content-end mb-2">
            <input
              type="search"
              placeholder="Search by domain & email..."
              className="form-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.trimStart())}
              style={{ width: "100%", maxWidth: "220px" ,fontSize: "13px"}}
            />
            {searchTerm === "" && (
              <div
                className="position-absolute"
                style={{
                  top: "50%",
                  right: "13px",
                  transform: "translateY(-50%)",
                  color: "#666",
                }}
              >
                <FaSearch />
              </div>
            )}
          </div>
          <div className="table-scroll-wrapper">
            <table className="partners-table">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Domain</th>
                  <th>Email</th>
                  <th>Risk Score</th>
                  <th>Report Status</th>
                  <th>Scan Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td
                      colSpan="12"
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#666",
                      }}
                    >
                      <div className="d-flex flex-column align-items-center">
                        <div className="spinner"></div>
                        <p>Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td
                      colSpan="12"
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#dc3545",
                      }}
                    >
                      {error}
                    </td>
                  </tr>
                ) : scans?.length > 0 ? (
                  scans?.map((scan, index) => (
                    <tr key={`${scan?._id}-${index}`}>
                      <td>{(currentPage - 1) * 10 + index + 1}</td>
                      <td>{scan?.domain}</td>
                      <td>{scan?.email}</td>
                      <td>{scan?.overAllRiskScore || 0}</td>
                      <td>
                        <span
                          className={`badge ${
                            scan?.reportSend ? "bg-success" : "bg-warning"
                          }`}
                        >
                          {scan?.reportSend ? "Sent" : "Pending"}
                        </span>
                      </td>
                      <td>
                        {scan?.createdAt
                          ? new Date(scan.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>
                        {scan?.pdfUrl ? (
                          <a
                            href={scan?.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaDownload
                              style={{ cursor: "pointer", color: "#007bff" }}
                              title="Download Report"
                            />
                          </a>
                        ) : (
                          <span
                            style={{ color: "#ccc" }}
                            title="Report not available"
                          >
                            -
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="12"
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#666",
                      }}
                    >
                      No scans found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={pagination?.totalPages || 1}
            totalItems={pagination?.totalItems || 0}
            itemsPerPage={pagination?.itemsPerPage || 10}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default MyDomainScan;

